import {
  advancementTypes,
  eventsModuleName,
  rsvpCodes,
} from '@shared/constants';

export const moduleName = eventsModuleName;

export const cloneEventFields = [
  'eventType',
  'location',
  'startTime',
  'endTime',
  'mapUrl',
  'description',
  'demographicLookupShort',
];

export const recurrenceTypesIds = Object.freeze({
  DAILY: 1,
  DAILY_WEEKDAYS: 2,
  WEEKLY_ONE_DAY: 3,
  WEEKLY_MULTIPLE_DAYS: 4,
  MONTHLY: 5,
  YEARLY: 6,
  /** Monthly (day) */
  MONTHLY_NTH_DAY: 7,
  /** Yearly (day) */
  YEARLY_NTH_DAY: 8,
  /**
   * Monthly multiple days
   * This will require to send multipleDaysOfTheMonth=[1,31] */
  MONTHLY_MULTIPLE_DAYS: 9,
});

export const recurrenceTypes = [
  {
    id: recurrenceTypesIds.DAILY,
    short: 'Daily',
    long: 'Occurs Daily',
    rangeUnit: 'day',
    maxRangeCount: 31,
  },
  {
    id: recurrenceTypesIds.DAILY_WEEKDAYS,
    short: 'Daily weekdays',
    long: 'Occurs daily, only on Weekdays',
    rangeUnit: 'day',

    maxRangeCount: 31,
  },
  {
    id: recurrenceTypesIds.WEEKLY_ONE_DAY,
    short: 'Weekly one day',
    long: 'Occurs once a Week',
    rangeUnit: 'week',
    maxRangeCount: 104, // around 1 year 11 months
  },
  {
    id: recurrenceTypesIds.WEEKLY_MULTIPLE_DAYS,
    short: 'Weekly multiple days',
    long: 'Occurs on selected days in a Week',
    rangeUnit: 'week',
    maxRangeCount: 104,
  },
  {
    id: recurrenceTypesIds.MONTHLY,
    short: 'Monthly (date)',
    long: 'Occurs once a Month',
    rangeUnit: 'month',
    maxRangeCount: 24,
  },
  {
    id: recurrenceTypesIds.MONTHLY_NTH_DAY,
    short: 'Monthly (day)',
    long: 'Occurs on the nth Weekday of a month',
    rangeUnit: 'month',
    maxRangeCount: 24,
  },
  {
    id: recurrenceTypesIds.MONTHLY_MULTIPLE_DAYS,
    short: 'Monthly multiple days',
    long: 'Occurs on selected days in a Month',
    rangeUnit: 'month',
    maxRangeCount: 60, // 5 years
  },
  {
    id: recurrenceTypesIds.YEARLY,
    short: 'Yearly (date)',
    long: 'Occurs once a Year',
    rangeUnit: 'year',
    maxRangeCount: 5,
  },
  {
    id: recurrenceTypesIds.YEARLY_NTH_DAY,
    short: 'Yearly (day)',
    long: 'Occurs on the nth Weekday of one month in the year',
    rangeUnit: 'year',
    maxRangeCount: 5,
  },
];

export const recurrenceDays = [
  { id: 1, text: 'Su', label: 'Sunday' },
  { id: 2, text: 'M', label: 'Monday' },
  { id: 3, text: 'Tu', label: 'Tuesday' },
  { id: 4, text: 'W', label: 'Wednesday' },
  { id: 5, text: 'Th', label: 'Thursday' },
  { id: 6, text: 'F', label: 'Friday' },
  { id: 7, text: 'Sa', label: 'Saturday' },
];

export const recurrenceMonths = [
  { id: 1, name: 'January', maxDays: 31 },
  { id: 2, name: 'February', maxDays: 28 },
  { id: 3, name: 'March', maxDays: 31 },
  { id: 4, name: 'April', maxDays: 30 },
  { id: 5, name: 'May', maxDays: 31 },
  { id: 6, name: 'Jun', maxDays: 30 },
  { id: 7, name: 'July', maxDays: 31 },
  { id: 8, name: 'August', maxDays: 31 },
  { id: 9, name: 'September', maxDays: 30 },
  { id: 10, name: 'October', maxDays: 31 },
  { id: 11, name: 'November', maxDays: 30 },
  { id: 12, name: 'December', maxDays: 31 },
];

export const recurrenceNthDays = [
  { value: 'first', label: 'First' },
  { value: 'second', label: 'Second' },
  { value: 'third', label: 'Third' },
  { value: 'fourth', label: 'Fourth' },
  { value: 'last', label: 'Last' },
];

export const userEventStatus = Object.freeze({
  NEW: 'new',
  OLD: 'old',
  DELETED: 'deleted',
  REMOVED: 'removed',
});

export const defaultRsvpUser = Object.freeze({
  attended: false,
  primaryLeader: false,
  status: userEventStatus.NEW,
});

export const defaultRsvpEventUser = Object.freeze({
  primaryLeader: false,
  status: userEventStatus.NEW,
});

export const attendeesTypes = Object.freeze({
  ATTENDING: 'attending',
  NOT_ATTENDING: 'notAttending',
  MAYBE: 'maybe',
  NO_RESPONSE: 'noResponse',
});

export const attendeesTypesAction = Object.freeze({
  ATTENDING: 'attendingAction',
  NOT_ATTENDING: 'notAttendingAction',
  MAYBE: 'maybeAction',
  NO_RESPONSE: 'noResponseAction',
});

export const typesAction = Object.freeze({
  [attendeesTypes.ATTENDING]: attendeesTypesAction.ATTENDING,
  [attendeesTypes.NOT_ATTENDING]: attendeesTypesAction.NOT_ATTENDING,
  [attendeesTypes.MAYBE]: attendeesTypesAction.MAYBE,
  [attendeesTypes.NO_RESPONSE]: attendeesTypesAction.NO_RESPONSE,
});

export const rsvpTypes = Object.freeze({
  ATTENDING: true,
  NOT_ATTENDING: false,
  MAYBE: null,
});

export const rsvpOtherActions = Object.freeze({
  ATTENDED: 'attended',
  NOT_ATTENDED: 'notAttended',
  REMOVE: 'remove',
});

export const dividerKey = Object.freeze({
  ATTENDED: 'attendedKey',
  NOT_ATTENDED: 'notAttendedKey',
  REMOVE: 'removeKey',
  ATTENDING: 'attendingKey',
  NOT_ATTENDING: 'notAttendingKey',
  MAYBE: 'maybeKey',
});

export const attendedActions = Object.freeze({
  [rsvpOtherActions.ATTENDED]: true,
  [rsvpOtherActions.NOT_ATTENDED]: false,
});

export const reminderRsvpSetting = Object.freeze({
  YES: 'yes',
  NO: 'no',
  ALL: 'all',
  MAYBE: 'maybe',
  YES_N_MAYBE: 'yes and maybe',
});

export const requirementsTypesNames = Object.freeze({
  [advancementTypes.MERIT_BADGES]: 'Merit Badges',
  [advancementTypes.RANKS]: 'Ranks',
  [advancementTypes.AWARDS]: 'Awards',
  [advancementTypes.ADVENTURES]: 'Adventures',
});

export const requirementsTypesValues = Object.freeze({
  [advancementTypes.MERIT_BADGES]: 'Merit Badge',
  [advancementTypes.RANKS]: 'Rank',
  [advancementTypes.AWARDS]: 'Award',
  [advancementTypes.ADVENTURES]: 'Adventure',
});

export const requirementsTypesIds = Object.freeze({
  'Merit Badge': advancementTypes.MERIT_BADGES,
  Rank: advancementTypes.RANKS,
  Award: advancementTypes.AWARDS,
  Adventure: advancementTypes.ADVENTURES,
});

export const advancementsSteps = Object.freeze({
  SELECT_ADVANCEMENT: 'selectAdvancements',
  SELECT_REQUIREMENTS: 'selectRequirements',
});

export const advancementsStepsOrder = [
  advancementsSteps.SELECT_ADVANCEMENT,
  advancementsSteps.SELECT_REQUIREMENTS,
];

export const attendeeTypeToRsvpValues = Object.freeze({
  [attendeesTypes.ATTENDING]: rsvpTypes.ATTENDING,
  [attendeesTypes.NOT_ATTENDING]: rsvpTypes.NOT_ATTENDING,
  [attendeesTypes.MAYBE]: rsvpTypes.MAYBE,
});

export const attendeeTypeToRsvpCode = Object.freeze({
  [attendeesTypes.ATTENDING]: rsvpCodes.YES,
  [attendeesTypes.NOT_ATTENDING]: rsvpCodes.NO,
  [attendeesTypes.MAYBE]: rsvpCodes.MAYBE,
  [attendeesTypes.NO_RESPONSE]: rsvpCodes.NO_RESPONSE,
});

export const rsvpCodeToattendeeType = Object.freeze({
  [rsvpCodes.YES]: attendeesTypes.ATTENDING,
  [rsvpCodes.NO]: attendeesTypes.NOT_ATTENDING,
  [rsvpCodes.MAYBE]: attendeesTypes.MAYBE,
  [rsvpCodes.NO_RESPONSE]: attendeesTypes.NO_RESPONSE,
});

export const sortTags = Object.freeze({
  FIRST_NAME: 'firstName',
  LAST_NAME: 'lastName',
  NICKNAME: 'nickName',
});

export const sortTranslations = Object.freeze({
  [sortTags.FIRST_NAME]: 'firstName',
  [sortTags.LAST_NAME]: 'lastName',
  [sortTags.NICKNAME]: 'lastName',
});

export const sortTypeLabel = Object.freeze({
  firstName: 'First Name',
  lastName: 'Last Name',
});

export const groupTags = Object.freeze({
  IS_ADULT: 'isAdult',
  NONE: 'none',
  RESERVATION_STATUS: 'rsvp',
});

export const groupTranslations = Object.freeze({
  [groupTags.IS_ADULT]: 'memberType',
  [groupTags.PRIMARY_LEADER]: 'roleType',
  [groupTags.NONE]: 'noFilter',
  [groupTags.RESERVATION_STATUS]: 'reservationStatus',
});

export const partitionNames = Object.freeze({
  [groupTags.NONE]: ['none', 'all'],
  [groupTags.PRIMARY_LEADER]: ['leadership', 'nonLeaders'],
  [groupTags.RESERVATION_STATUS]: [
    'confirmed',
    'maybe',
    'declined',
    'noResponse',
  ],
  [groupTags.IS_ADULT]: ['youths', 'denChiefs', 'adults'],
});

export const attendeeGroups = Object.freeze({
  YOUTHS: 'youths',
  DEN_CHIEFS: 'denChiefs',
  ADULTS: 'adults',
});

// groups and sorters work in function of
// group and sort tags respectively
export const groups = Object.keys(groupTags).map(tag => ({
  value: groupTags[tag],
  translation: groupTranslations[groupTags[tag]],
}));

export const sorters = Object.keys(sortTags).map(tag => ({
  value: sortTags[tag],
  translation: sortTranslations[sortTags[tag]],
}));

export const fieldState = Object.freeze({
  DISABLED: 'Disabled',
  ENABLED: 'Enabled',
  REQUIRED: 'Required',
});

export const camelColName = Object.freeze({
  UNIT_ID: 'unitId',
});

export const denSortOrder = Object.freeze([
  'lions',
  'tigers',
  'wolves',
  'bears',
  'webelos',
  'aol',
]);

const AdminPositions = Object.freeze({
  DEN_ADMIN: 151,
  PATROL_ADMIN: 152,
  PACK_ADMIN: 153,
  TROOP_ADMIN: 154,
  TEAM_ADMIN: 157,
  CREW_ADMIN: 158,
  COUNCIL_ADMIN: 206,
  SHIP_ADMIN: 213,
});

export const extraType = Object.freeze({
  UNASSIGNED: 'Unassigned',
});

export const AdminPositionIds = Object.values(AdminPositions);

export const personSelectOptions = [
  { value: 'SCOUTS', label: 'All Youth', fieldDecorator: 'youthAudience' },
  { value: 'LEADERS', label: 'All Leaders', fieldDecorator: 'leadersAudience' },
  {
    value: 'PARENTS',
    label: 'All Parents/Guardians',
    fieldDecorator: 'parentsAudience',
  },
];

export const personSelectOptionsType = Object.freeze({
  SCOUTS: 'SCOUTS',
  LEADERS: 'LEADERS',
  PARENTS: 'PARENTS',
});

export const commentLength = 500;

// maxRemindersHours is dictated by the back end
export const maxRemindersHours = 17521;

export const commentEmailAllowedPositions = [
  'Key 3 Delegate',
  'Key 3',
  'Cubmaster',
  'Scoutmaster',
  'Venturing Crew Advisor',
  'Skipper',
  'Committee Chairman',
  'Charter Organization Representative',
  'Chartered Organization Rep Delegate',
];

export const Demographics = Object.freeze({
  YOUTHS: 'YTH',
  PARENTS: 'PNT',
  LEADERS: 'LED',
  YOUTHS_N_PARENTS: 'YP',
  YOUTHS_N_LEADERS: 'YL',
  PARENTS_N_LEADERS: 'PL',
  ALL: 'ALL',
  NA: 'NA',
});

export const OtherEventTypeId = 16;
